import type { InfiniteData, QueryClient } from '@tanstack/react-query';
import type {
  ConversationFilter,
  ConversationItem,
  ConversationListResponse,
  Role,
} from '@chatapp-inbox/shared';

/**
 * Cirurgia nas listas de conversas já carregadas (cache `['conversations', filtro]`, formato
 * `InfiniteData<ConversationListResponse>`) a partir de um evento do tempo real, sem refazer a busca.
 *
 * A ordem do servidor é "mais recente primeiro": `COALESCE(last_message_at, created_at) DESC, id DESC`.
 * Uma conversa só "sobe" (chega mensagem) ou muda de grupo (atribuição); por isso basta, em cada
 * lista: tirar a conversa de onde estava e, se ela pertence àquela aba, pôr na posição certa DENTRO
 * do trecho já carregado. Se a posição cai abaixo do que foi carregado ela não entra: a página
 * seguinte a traz quando for pedida (a busca por cursor é consistente com o estado atual do banco).
 */

type Pages = InfiniteData<ConversationListResponse, string | null>;

export interface Viewer {
  id: string;
  role: Role;
}

const LISTS = ['conversations'] as const;

/** Ordem das listas: mais recente primeiro; empate pelo id (como no servidor). */
function compare(a: ConversationItem, b: ConversationItem): number {
  const ta = a.lastMessageAt ?? '';
  const tb = b.lastMessageAt ?? '';
  if (ta !== tb) return ta > tb ? -1 : 1;
  if (a.id !== b.id) return a.id > b.id ? -1 : 1;
  return 0;
}

/**
 * A conversa aparece na aba `filter` para esta pessoa? As abas de trabalho só têm conversas abertas; a
 * aba "Encerradas", só as encerradas (docs/conversation-status.md).
 */
export function belongsToFilter(
  filter: ConversationFilter,
  item: Pick<ConversationItem, 'assignedTo' | 'status'>,
  viewer: Viewer,
): boolean {
  if (filter === 'closed') return item.status === 'closed';
  if (item.status === 'closed') return false;
  if (filter === 'mine') return item.assignedTo?.id === viewer.id;
  if (filter === 'unassigned') return item.assignedTo === null;
  // "Todas": o servidor só entrega eventos de conversas que a pessoa pode ver.
  return true;
}

function removeFrom(data: Pages, id: string): { data: Pages; removed: ConversationItem | null } {
  let removed: ConversationItem | null = null;
  const pages = data.pages.map((page) => {
    if (!page.conversations.some((c) => c.id === id)) return page;
    return {
      ...page,
      conversations: page.conversations.filter((c) => {
        if (c.id !== id) return true;
        removed ??= c;
        return false;
      }),
    };
  });
  return { data: removed ? { ...data, pages } : data, removed };
}

/** Insere `item` na posição ordenada do trecho carregado. Devolve null se cai abaixo dele. */
function insertSorted(data: Pages, item: ConversationItem): Pages | null {
  const pages = data.pages;
  for (let p = 0; p < pages.length; p++) {
    const list = pages[p]!.conversations;
    const last = list.at(-1);
    const isLastPage = p === pages.length - 1;
    // Dentro do alcance desta página: vem antes (ou junto) do último item dela. Na última página
    // SEM próxima página (`nextCursor` nulo) tudo o que sobra pertence ao fim da lista.
    const fits = last ? compare(item, last) <= 0 : true;
    const endOfList = isLastPage && pages[p]!.nextCursor === null;
    if (!fits && !endOfList) continue;
    let index = list.findIndex((c) => compare(item, c) < 0);
    if (index === -1) index = list.length;
    const conversations = [...list.slice(0, index), item, ...list.slice(index)];
    return {
      ...data,
      pages: pages.map((page, i) => (i === p ? { ...page, conversations } : page)),
    };
  }
  return null;
}

/** Resultado para uma lista: os dados novos, ou `undefined` se nada mudou. */
function applyToList(
  data: Pages,
  filter: ConversationFilter,
  item: ConversationItem,
  viewer: Viewer,
): { data: Pages; needsRefresh: boolean } | null {
  const { data: without, removed } = removeFrom(data, item.id);
  if (!belongsToFilter(filter, item, viewer)) {
    return removed ? { data: without, needsRefresh: false } : null;
  }
  if (item.lastMessageAt === null) {
    // Sem data da última mensagem não dá para saber a posição (o servidor usa a criação da conversa).
    // Se ela já estava na lista, fica no lugar; senão a lista é refeita.
    if (!removed) return { data, needsRefresh: true };
    return { data: replaceInPlace(data, item), needsRefresh: false };
  }
  const inserted = insertSorted(without, item);
  if (inserted) return { data: inserted, needsRefresh: false };
  // Cai abaixo do trecho carregado: sai daqui (a página seguinte a trará).
  return removed ? { data: without, needsRefresh: false } : null;
}

function replaceInPlace(data: Pages, item: ConversationItem): Pages {
  return {
    ...data,
    pages: data.pages.map((page) =>
      page.conversations.some((c) => c.id === item.id)
        ? { ...page, conversations: page.conversations.map((c) => (c.id === item.id ? item : c)) }
        : page,
    ),
  };
}

/**
 * Aplica a versão nova de uma conversa a TODAS as listas em cache. Não muda "quando os dados foram
 * buscados": a lista pode estar sem itens abaixo do trecho carregado e continua tão (des)atualizada
 * quanto estava. Devolve se alguma lista precisa ser refeita (posição desconhecida).
 */
export function applyConversationToLists(
  queryClient: QueryClient,
  item: ConversationItem,
  viewer: Viewer,
): { needsRefresh: boolean } {
  let needsRefresh = false;
  for (const [key, cached] of queryClient.getQueriesData<Pages>({ queryKey: LISTS })) {
    if (!cached) continue;
    const filter = key[1] as ConversationFilter;
    const result = applyToList(cached, filter, item, viewer);
    if (!result) continue;
    if (result.needsRefresh) needsRefresh = true;
    if (result.data === cached) continue;
    const updatedAt = queryClient.getQueryState(key)?.dataUpdatedAt;
    queryClient.setQueryData<Pages>(key, result.data, updatedAt ? { updatedAt } : undefined);
  }
  return { needsRefresh };
}

/** A conversa saiu da visão da pessoa: tira de todas as listas. */
export function removeConversationFromLists(queryClient: QueryClient, id: string): void {
  for (const [key, cached] of queryClient.getQueriesData<Pages>({ queryKey: LISTS })) {
    if (!cached) continue;
    const { data, removed } = removeFrom(cached, id);
    if (!removed) continue;
    const updatedAt = queryClient.getQueryState(key)?.dataUpdatedAt;
    queryClient.setQueryData<Pages>(key, data, updatedAt ? { updatedAt } : undefined);
  }
}
